import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Chip, Container, Grid, Stack, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { useParams } from 'react-router-dom';
import { getOrder, updateOrderStatus, reorder } from '../services/Common/orders.service';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

export default function OrderDetailsPage() {
  const { id } = useParams();
  // const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [updating, setUpdating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getOrder(id);
        // service returns inner payload (data) or raw object
        const item = res || res?.data || res?.order || res?.result || null;
        setOrder(item);
      } catch (e) { toast.error(e?.response?.data?.message || 'Failed to load order'); }
    };
    load();
  }, [id]);

  const onUpdate = async (status) => {
    try {
      setUpdating(true);
      await updateOrderStatus(id, status);
      setOrder(o => ({ ...o, status }));
      toast.success('Status updated');
    } catch (e) { toast.error(e?.response?.data?.message || 'Update failed'); }
    finally { setUpdating(false); }
  };

  const onReorder = async () => {
    try {
      const res = await reorder(id)
      const newOrder = res || res?.data || res?.order || null
      if (newOrder && newOrder._id) {
        toast.success('Reorder placed')
        navigate(`/orders/${newOrder._id}`)
      } else {
        toast.success('Reorder placed')
      }
    } catch (e) { toast.error(e?.response?.data?.message || 'Reorder failed') }
  }

  if (!order) return null;

  return (
    <>
      <Helmet><title>Order #{order.orderNo} | Trooper</title></Helmet>
      <Container>
        <Stack direction='row' alignItems='center' justifyContent='space-between' mb={3}>
          <Typography variant='h4'>Order #{order.orderNo}</Typography>
          <Stack direction='row' spacing={1}>
            {['Pending','Confirmed','Processing','Shipped','Delivered','Cancelled'].map(s => (
              <Button key={s} size='small' variant='contained' disabled={updating || order.status===s} onClick={() => onUpdate(s)}>{s}</Button>
            ))}
            <Button size='small' color='secondary' variant='outlined' onClick={onReorder}>Reorder</Button>
          </Stack>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant='h6'>Summary</Typography>
              <Box>Status: <Chip label={order.status} /></Box>
              <Box>Amount: {order.total ?? order.subtotal}</Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant='h6'>Customer</Typography>
              <Box>{order.customer?.name}</Box>
              <Box>{order.customer?.email} {order.customer?.phone ? ` | ${order.customer.phone}` : ''}</Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant='subtitle2'>Billing</Typography>
                <Box>{order.billing?.address?.line1 || order.customer?.address?.line1 || order.billing?.line1 || ''}</Box>
                <Box>{order.billing?.address?.city || order.customer?.address?.city || order.billing?.city || ''} {order.billing?.address?.postalCode || order.billing?.address?.zip || order.customer?.address?.postalCode || order.billing?.postalCode || ''}</Box>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant='subtitle2'>Shipping Address</Typography>
                <Box>{order.shippingAddress?.line1}</Box>
                <Box>{order.shippingAddress?.city} {order.shippingAddress?.postalCode || order.shippingAddress?.zip}</Box>
              </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant='h6' sx={{ mt: 2, mb: 1 }}>Products</Typography>
            <TableContainer component={Paper}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align='right'>Qty</TableCell>
                    <TableCell align='right'>Price</TableCell>
                    <TableCell align='right'>Subtotal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(order.items || []).map((it, idx) => (
                    <TableRow key={it.productId || idx}>
                      <TableCell>{it.name || it.title || it.productName || 'Product'}</TableCell>
                      <TableCell align='right'>{it.quantity}</TableCell>
                      <TableCell align='right'>{Number(it.price).toFixed(2)}</TableCell>
                      <TableCell align='right'>{Number(it.subtotal ?? (Number(it.price) * Number(it.quantity || 1))).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  {(!order.items || order.items.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} align='center'>No products found in this order</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
